import React, { useMemo, useState } from 'react';
import {
  Assignment,
  Business,
  CheckCircle,
  Description,
  History,
  Home,
  Person,
  Schedule,
  Timeline,
  Warning,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useParams } from 'react-router-dom';

import { useGetContractQuery } from '@/apis/extended/contractApi';
import { useGetContractPipelineQuery } from '@/apis/extended/pipelineApi';
import { useGetContractProposalsQuery } from '@/apis/extended/proposalApi';
import GlassCard from '@/components/ui/GlassCard';
import { buildAdminContractComparisonRows } from '@/utils/adminContractComparison';
import {
  calculateContractTimeline,
  getOverdueCount,
  getTimelineProgress,
} from '@/utils/contractTimeline';
import DocumentCenter from '../PostAgreement/DocumentCenter';
import InspectionProperty from '../PostAgreement/InspectionProperty';
import TaskManagement from '../PostAgreement/TaskManagement';
import AddendumTemplatesPanel from './AddendumTemplatesPanel';
import { AdminClosingPanel } from './AdminClosingPanel';
import ContractComparisonTable from './ContractComparisonTable';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminPropertyDashboard: React.FC = () => {
  const theme = useTheme();
  const { contractId } = useParams<{ contractId: string }>();
  const [activeTab, setActiveTab] = useState(0);

  const { data: contract, isLoading: contractLoading } = useGetContractQuery(
    contractId || '',
    {
      // Ensure we get populated buyer and seller data
      skip: !contractId,
    }
  );
  const { data: pipelineData } = useGetContractPipelineQuery(contractId || '', {
    skip: !contractId,
  });
  const { data: proposalsResponse } = useGetContractProposalsQuery(
    { contractId: contractId || '' },
    { skip: !contractId }
  );

  // Calculate timeline data
  const timelineEvents = useMemo(() => {
    if (!contract) return [];
    return calculateContractTimeline(contract);
  }, [contract]);

  const progress = useMemo(() => {
    return getTimelineProgress(timelineEvents);
  }, [timelineEvents]);

  const overdueCount = useMemo(() => {
    return getOverdueCount(timelineEvents);
  }, [timelineEvents]);

  const hasInspectionTab =
    contract?.contingency?.inspectionContingency === 'reserve_the_right';
  const documentsTabIndex = 3;
  const templatesTabIndex = 4;
  const inspectionTabIndex = hasInspectionTab ? 5 : null;
  const historyTabIndex = hasInspectionTab ? 6 : 5;

  const contractClauses = useMemo(() => {
    if (!contract || !contractId) return [];
    return buildAdminContractComparisonRows(
      contract,
      contractId,
      proposalsResponse?.proposals ?? []
    );
  }, [contract, contractId, proposalsResponse?.proposals]);

  if (contractLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Loading property dashboard...
        </Typography>
      </Box>
    );
  }

  if (!contract) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Contract not found</Alert>
      </Box>
    );
  }

  const formatAddress = (addr: unknown): string => {
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    if (typeof addr === 'object') {
      const a = addr as any;
      return (
        (typeof a.fullAddress === 'string' && a.fullAddress) ||
        [a.street, a.city, a.state, a.zip].filter(Boolean).join(', ') ||
        ''
      );
    }
    return String(addr);
  };

  const daysToClosing = contract?.offerSpecifics?.closingDate
    ? Math.ceil(
        (new Date(contract.offerSpecifics.closingDate).getTime() - Date.now()) /
          (1000 * 60 * 60 * 24)
      )
    : 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <GlassCard variant="bold" sx={{ mb: 3, color: 'text.primary' }}>
        <CardHeader
          title={
            <Typography variant="h4" color="text.primary" fontWeight={700}>
              Contract Dashboard
            </Typography>
          }
          subheader={
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" color="text.primary" fontWeight={600}>
                {formatAddress(contract.product?.address) ||
                  contract.buyerInfo?.street ||
                  'Property Address Not Available'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Contract ID: {contract.contractId || contract._id}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`Status: ${contract.contractStatus || 'Active'}`}
                  color="primary"
                  icon={<Business />}
                />
                <Chip
                  label={`Days to Close: ${daysToClosing}`}
                  color={
                    daysToClosing < 7
                      ? 'error'
                      : daysToClosing < 14
                      ? 'warning'
                      : 'success'
                  }
                  icon={<Schedule />}
                />
                <Chip
                  label={`Progress: ${Math.round(progress)}%`}
                  color="info"
                  icon={<CheckCircle />}
                />
                {overdueCount > 0 && (
                  <Chip
                    label={`${overdueCount} Overdue`}
                    color="error"
                    icon={<Warning />}
                  />
                )}
              </Box>
            </Box>
          }
        />
      </GlassCard>

      {pipelineData?.data && (
        <GlassCard variant="subtle" sx={{ mb: 3, color: 'text.primary' }}>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="h6" fontWeight={600} color="text.primary">
                Pipeline Status
              </Typography>
              <Chip
                label={
                  pipelineData.data.stages.find((s) => s.isCurrent)?.label ||
                  pipelineData.data.currentStageKey
                }
                color="primary"
                size="small"
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={
                pipelineData.data.stages.find((s) => s.isCurrent)?.progress
                  .percent ?? 0
              }
              sx={{ height: 8, borderRadius: 4, mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              Blockers:{' '}
              {pipelineData.data.stages.find((s) => s.isCurrent)?.progress
                .blockers.length ?? 0}
            </Typography>
          </CardContent>
        </GlassCard>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <GlassCard
            variant="subtle"
            sx={{
              color: 'text.primary',
              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                fontWeight={600}
                color="text.primary"
              >
                Contract Progress
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 8, borderRadius: 4, mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                {timelineEvents.filter((e) => e.status === 'completed').length}{' '}
                of {timelineEvents.length} milestones completed
              </Typography>
            </CardContent>
          </GlassCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <GlassCard
            variant="subtle"
            sx={{
              color: 'text.primary',
              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                fontWeight={600}
                color="text.primary"
              >
                Parties Involved
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                    <Person fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Buyer"
                    secondary={
                      contract.buyer?.name ||
                      contract.buyerInfo?.name ||
                      'Not specified'
                    }
                    primaryTypographyProps={{
                      color: 'text.primary',
                      fontWeight: 500,
                      variant: 'body2',
                    }}
                    secondaryTypographyProps={{ color: 'text.secondary' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                    <Person fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Seller"
                    secondary={contract.user?.name || 'Not specified'}
                    primaryTypographyProps={{
                      color: 'text.primary',
                      fontWeight: 500,
                      variant: 'body2',
                    }}
                    secondaryTypographyProps={{ color: 'text.secondary' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                    <Person fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Agent"
                    secondary="Property Agent"
                    primaryTypographyProps={{
                      color: 'text.primary',
                      fontWeight: 500,
                      variant: 'body2',
                    }}
                    secondaryTypographyProps={{ color: 'text.secondary' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </GlassCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <GlassCard
            variant="subtle"
            sx={{
              color: 'text.primary',
              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                fontWeight={600}
                color="text.primary"
              >
                Key Dates
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText
                    primary="Contract Created"
                    secondary={
                      contract.createdAt
                        ? new Date(contract.createdAt).toLocaleDateString()
                        : 'N/A'
                    }
                    primaryTypographyProps={{
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{ color: 'text.secondary' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText
                    primary="Closing Date"
                    secondary={
                      contract.offerSpecifics?.closingDate
                        ? new Date(
                            contract.offerSpecifics.closingDate
                          ).toLocaleDateString()
                        : 'TBD'
                    }
                    primaryTypographyProps={{
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{ color: 'text.secondary' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText
                    primary="Last Updated"
                    secondary={
                      contract.updatedAt
                        ? new Date(contract.updatedAt).toLocaleDateString()
                        : 'N/A'
                    }
                    primaryTypographyProps={{
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{ color: 'text.secondary' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </GlassCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <GlassCard
            variant="subtle"
            sx={{
              color: 'text.primary',
              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                fontWeight={600}
                color="text.primary"
              >
                Contract Value
              </Typography>
              <Typography variant="h4" color="primary" fontWeight={700}>
                $
                {(
                  contract.offerSpecifics?.offerPrice ||
                  contract.offerSpecifics?.purchasePrice ||
                  0
                ).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Purchase Price
              </Typography>
              {contract.offerSpecifics?.loanAmount && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Loan Amount: $
                  {contract.offerSpecifics.loanAmount.toLocaleString()}
                </Typography>
              )}
              {contract.offerSpecifics?.downPayment && (
                <Typography variant="body2" color="text.secondary">
                  Down Payment: $
                  {contract.offerSpecifics.downPayment.toLocaleString()}
                </Typography>
              )}
            </CardContent>
          </GlassCard>
        </Grid>
      </Grid>

      {/* Buyer Financial Information */}
      {contract.buyer?.loanInfo && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <GlassCard variant="medium" sx={{ color: 'text.primary' }}>
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="text.primary"
                  >
                    Buyer Financial Information
                  </Typography>
                }
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="h4"
                        sx={{ color: theme.palette.mainColors.primary }}
                      >
                        $
                        {contract.buyer.loanInfo.monthlyIncome?.toLocaleString() ||
                          'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Monthly Income
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="h4"
                        sx={{ color: theme.palette.mainColors.secondary }}
                      >
                        {contract.buyer.loanInfo.creditScore || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Credit Score
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="h4"
                        sx={{ color: theme.palette.mainColors.accent }}
                      >
                        $
                        {contract.buyer.loanInfo.monthlyDebt?.toLocaleString() ||
                          'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Monthly Debt
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="h4"
                        sx={{ color: theme.palette.mainColors.success }}
                      >
                        {contract.buyer.loanInfo.monthlyIncome &&
                        contract.buyer.loanInfo.monthlyDebt
                          ? Math.round(
                              (contract.buyer.loanInfo.monthlyDebt /
                                contract.buyer.loanInfo.monthlyIncome) *
                                100
                            )
                          : 'N/A'}
                        %
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Debt-to-Income Ratio
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>
      )}

      {/* Main Dashboard Tabs */}
      <GlassCard
        variant="medium"
        sx={{
          color: 'text.primary',
          border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              '& .MuiTab-root': {
                color: 'text.secondary',
                fontWeight: 500,
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 600,
                },
              },
            }}
          >
            <Tab label="Overview" icon={<Home />} iconPosition="start" />
            <Tab label="Timeline" icon={<Timeline />} iconPosition="start" />
            <Tab label="Tasks" icon={<Assignment />} iconPosition="start" />
            <Tab
              label="Documents"
              icon={<Description />}
              iconPosition="start"
            />
            <Tab
              label="Addendum Templates"
              icon={<Description />}
              iconPosition="start"
            />
            {hasInspectionTab && (
              <Tab label="Inspection" icon={<Home />} iconPosition="start" />
            )}
            <Tab label="History" icon={<History />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Overview Tab */}
        <TabPanel value={activeTab} index={0}>
          <ContractComparisonTable
            clauses={contractClauses}
            onClauseClick={(clause) => {
              console.log('Clause clicked:', clause);
              // Handle clause click - could open detailed view or edit modal
            }}
          />
        </TabPanel>

        {/* Timeline Tab — closing ops + milestones */}
        <TabPanel value={activeTab} index={1}>
          <Typography
            variant="h6"
            gutterBottom
            fontWeight={600}
            color="text.primary"
          >
            Closing orchestration
          </Typography>
          {contractId ? (
            <AdminClosingPanel contractId={contractId} />
          ) : (
            <Alert severity="info">No contract linked.</Alert>
          )}
        </TabPanel>

        {/* Tasks Tab */}
        <TabPanel value={activeTab} index={2}>
          <TaskManagement userRole="agent" contractId={contractId || ''} />
        </TabPanel>

        {/* Documents Tab */}
        <TabPanel value={activeTab} index={documentsTabIndex}>
          <DocumentCenter contractId={contractId || ''} userRole="agent" />
        </TabPanel>

        {/* Addendum Templates Tab */}
        <TabPanel value={activeTab} index={templatesTabIndex}>
          <AddendumTemplatesPanel />
        </TabPanel>

        {/* Inspection Tab */}
        {hasInspectionTab && (
          <TabPanel value={activeTab} index={inspectionTabIndex}>
            <InspectionProperty
              contractId={contractId || ''}
              userRole="agent"
            />
          </TabPanel>
        )}

        {/* History Tab */}
        <TabPanel value={activeTab} index={historyTabIndex}>
          <Typography
            variant="h6"
            gutterBottom
            fontWeight={600}
            color="text.primary"
          >
            Contract History
          </Typography>
          <Alert severity="info">
            History view shows all changes made to the contract by all parties.
          </Alert>
        </TabPanel>
      </GlassCard>
    </Box>
  );
};

export default AdminPropertyDashboard;
